import { useState, useEffect } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useTranslation } from "react-i18next";

export const ProfileForm = () => {
	const { user, userProfile, refreshUserData } = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState({
		firstName: userProfile?.first_name || "",
		lastName: userProfile?.last_name || ""
	});
	const [hasChanges, setHasChanges] = useState(false);
	const { t } = useTranslation();

	useEffect(() => {
		// Check if form data differs from original user profile data
		const isDifferent = 
			formData.firstName !== (userProfile?.first_name || "") || 
			formData.lastName !== (userProfile?.last_name || "");
		
		setHasChanges(isDifferent);
	}, [formData, userProfile]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!user) {
			toast.error("You must be logged in to update your profile");
			return;
		}
		
		if (!formData.firstName.trim()) {
			toast.error("First name is required");
			return;
		}
		
		if (!formData.lastName.trim()) {
			toast.error("Last name is required");
			return;
		}
		
		setIsLoading(true);
		
		try {
			const { error } = await supabase
				.from('profiles')
				.update({
					first_name: formData.firstName.trim(),
					last_name: formData.lastName.trim()
				})
				.eq('id', user.id);
				
			if (error) throw error;
			
			// Refresh user data to update the UI
			await refreshUserData();
			
			toast.success(t("settings.userSettings.profile.successMessage"));
		} catch (error) {
			console.error("Error updating profile:", error);
			toast.error(t("settings.userSettings.profile.errorMessage"));
		} finally {
			setIsLoading(false);
		}
	};
	
	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div className="space-y-4">
				<div className="space-y-2">
					<Label htmlFor="firstName">{t("settings.userSettings.profile.firstName")}</Label>
					<Input
						id="firstName"
						name="firstName"
						value={formData.firstName}
						onChange={handleChange}
						placeholder={t("settings.userSettings.profile.enterFirstName")}
						required
					/>
				</div>
				
				<div className="space-y-2">
					<Label htmlFor="lastName">{t("settings.userSettings.profile.lastName")}</Label>
					<Input
						id="lastName"
						name="lastName"
						value={formData.lastName}
						onChange={handleChange}
						placeholder={t("settings.userSettings.profile.enterLastName")}
						required
					/>
				</div>
			</div>
			
			{hasChanges && (
				<div className="flex justify-end">
					<Button type="submit" disabled={isLoading}>
						{isLoading ? t("common.saving") : t("settings.userSettings.profile.saveChanges")}
						<Save className="ml-2 h-4 w-4" />
					</Button>
				</div>
			)}
		</form>
	);
};